import type { CITY_FILTER_KEY, CITY_HOME_KEY } from '@/constants/common'
import type { SearchCity, SearchCityDefault } from '~/types/common'

type CookiesCityKey = typeof CITY_HOME_KEY | typeof CITY_FILTER_KEY

export function useLivingCity(cookiesCityKey: CookiesCityKey) {
  const { $dayjs } = useNuxtApp();
  const commonStore = useCommonStore()
  const authUserStore = useAuthUserStore()

  const {
    citiesDefault
  } = storeToRefs(commonStore)

  const { data: userData } = storeToRefs(authUserStore)
  const cityCookies = useCookie<SearchCityDefault | undefined>(cookiesCityKey, {
    expires: $dayjs().add(1, 'y').toDate(),
    path: '/'
  })

  const latestSelectedCity = shallowRef<SearchCityDefault>()

  const livingCity = computed(() => {
    return userData.value?.living_city
  })

  const citiesList = computed(() => {
    const list: SearchCityDefault[] = []

    const latestCity = latestSelectedCity.value
    const livingCityUuid = livingCity.value?.uuid

    const ignoredCity = new Set<string>()

    if (latestCity && latestCity.uuid !== livingCityUuid) {
      list.push(latestCity)
      ignoredCity.add(latestCity.uuid)
    }

    if (livingCity.value) {
      list.push({
        ...livingCity.value,
        short_name: getCityShortName(livingCity.value.name)
      })
      ignoredCity.add(livingCity.value.uuid)
    }

    citiesDefault.value.forEach((cityDefault: SearchCityDefault) => {
      if (!ignoredCity.has(cityDefault.uuid)) {
        list.push(cityDefault)
      }
    })

    return list
  })

  const getCityCookies = () => {
    const city = cityCookies.value

    if (city) {
      latestSelectedCity.value = city
    }
  }

  const setCityCookies = (city: SearchCity | null) => {
    if (city === null || !city.uuid) {
      return
    }

    cityCookies.value = {
      ...city,
      post_code: [],
      parent: city.parent ? {
        name: city.parent.name,
        uuid: city.parent.uuid,
        post_code: []
      } : undefined,
      short_name: getCityShortName(city.name)
    }
  }

  getCityCookies()

  return {
    latestSelectedCity,
    livingCity,
    citiesList,
    citiesDefault,
    setCityCookies,
    getCityShortName
  }
}
